import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../core/services/logger.service';
import {
    ArrayResponseEnvelopeOfKorberLaboratoryResponseDto,
    ArrayResponseEnvelopeOfRpwLaboratoryDto,
    ArrayResponseEnvelopeOfSapLaboratoryResponseDto,
    LaboratoryOption,
    ReplaceRpwUserLaboratoriesPayload,
    RpwUserLaboratoryItemPayload,
    RpwUserLaboratoryListItemResponse,
    SingleResponseEnvelopeOfRpwUserLaboratoryGroupedResponse,
} from '../models/laboratorio.model';

@Injectable({ providedIn: 'root' })
export class LaboratoriosService {
    /**
     * El API pagina con `pageSize` 10 por defecto, así que se pide un tamaño
     * amplio y se siguen solicitando páginas mientras vengan llenas. Así el
     * catálogo llega completo tanto si el servidor respeta el tamaño como si
     * lo limita a un máximo menor.
     */
    private static readonly PAGE_SIZE = 200;
    /** Tope defensivo para no encadenar peticiones indefinidamente. */
    private static readonly MAX_PAGES = 50;

    private readonly http = inject(HttpClient);
    private readonly logger = inject(LoggerService);

    // =========================================================================
    // Catálogos: GET /rpw/sap/laboratories + GET /rpw/korber/laboratories
    // =========================================================================

    /** Catálogo combinado de ambas fuentes, normalizado para la vista. */
    getCatalog(): Observable<LaboratoryOption[]> {
        return forkJoin({
            sap: this.getSapCatalog(),
            korber: this.getKorberCatalog(),
        }).pipe(map(({ sap, korber }) => [...sap, ...korber]));
    }

    getSapCatalog(): Observable<LaboratoryOption[]> {
        return this.fetchAllPages<ArrayResponseEnvelopeOfSapLaboratoryResponseDto>(
            `${environment.rpwApiUrl}/rpw/sap/laboratories`,
        ).pipe(
            map((items) =>
                items.map((lab) => ({
                    itmsGrpCod: String(lab.itmsGrpCod),
                    itmsGrpNam: (lab.itmsGrpNam ?? '').trim(),
                    type: 'Sap' as const,
                })),
            ),
            catchError((error) => {
                this.logger.error('LaboratoriosService: Failed to load SAP catalog', error);
                return of([]);
            }),
        );
    }

    getKorberCatalog(): Observable<LaboratoryOption[]> {
        return this.fetchAllPages<ArrayResponseEnvelopeOfKorberLaboratoryResponseDto>(
            `${environment.rpwApiUrl}/rpw/korber/laboratories`,
        ).pipe(
            map((items) =>
                items.map((lab) => ({
                    itmsGrpCod: String(lab.itmsGrpCod),
                    // Korber puede devolver el nombre nulo o en blanco.
                    itmsGrpNam: (lab.itmsGrpNam ?? '').trim(),
                    type: 'Korber' as const,
                })),
            ),
            catchError((error) => {
                this.logger.error('LaboratoriosService: Failed to load Korber catalog', error);
                return of([]);
            }),
        );
    }

    // =========================================================================
    // GET /rpw/user/laboratories — laboratorios del usuario autenticado
    // El API los resuelve con el `sub` del token, no lleva id en la ruta.
    // =========================================================================
    getMyLaboratories(): Observable<LaboratoryOption[]> {
        return this.http
            .get<ArrayResponseEnvelopeOfRpwLaboratoryDto>(
                `${environment.rpwApiUrl}/rpw/user/laboratories`,
            )
            .pipe(
                map((response) =>
                    (response.results ?? []).map((lab) => ({
                        itmsGrpCod: String(lab.itmsGrpCod),
                        itmsGrpNam: (lab.itmsGrpNam ?? '').trim(),
                        type: lab.type,
                    })),
                ),
                catchError((error) => {
                    this.logger.error('LaboratoriosService: Failed to load own laboratories', error);
                    return of([]);
                }),
            );
    }

    // =========================================================================
    // GET /rpw/user/{userId}/laboratories — laboratorios asignados a un usuario
    // =========================================================================
    getUserLaboratories(userId: number): Observable<RpwUserLaboratoryListItemResponse[]> {
        return this.http
            .get<SingleResponseEnvelopeOfRpwUserLaboratoryGroupedResponse>(
                `${environment.rpwApiUrl}/rpw/user/${userId}/laboratories`,
            )
            .pipe(
                map((response) =>
                    (response.result?.laboratories ?? []).map((lab) => ({
                        ...lab,
                        itmsGrpCod: String(lab.itmsGrpCod),
                    })),
                ),
                catchError((error) => {
                    this.logger.error(
                        `LaboratoriosService: Failed to load laboratories for user ${userId}`,
                        error,
                    );
                    return of([]);
                }),
            );
    }

    // =========================================================================
    // PUT /rpw/user/{userId}/laboratories — reemplaza la asignación completa
    // Incluye ambas fuentes: enviar solo una borraría las de la otra.
    // Una lista vacía elimina todos los laboratorios del usuario.
    // =========================================================================
    replaceUserLaboratories(
        userId: number,
        laboratories: RpwUserLaboratoryItemPayload[],
    ): Observable<boolean> {
        const payload: ReplaceRpwUserLaboratoriesPayload = { laboratories };

        return this.http
            .put(`${environment.rpwApiUrl}/rpw/user/${userId}/laboratories`, payload)
            .pipe(
                map(() => true),
                catchError((error) => {
                    this.logger.error(
                        `LaboratoriosService: Failed to save laboratories for user ${userId}`,
                        error,
                    );
                    return of(false);
                }),
            );
    }

    // =========================================================================
    // Utilidades internas
    // =========================================================================

    /**
     * Recorre las páginas de un catálogo hasta que una vuelva incompleta.
     *
     * El servidor puede limitar `pageSize` por debajo de lo pedido, así que el
     * tamaño real de página se deduce de la primera respuesta: mientras las
     * siguientes vengan igual de llenas, quedan más resultados por leer.
     */
    private fetchAllPages<T extends { results?: unknown[] }>(url: string): Observable<any[]> {
        const request = (page: number) =>
            this.http.get<T>(url, {
                params: new HttpParams()
                    .set('page', page)
                    .set('pageSize', LaboratoriosService.PAGE_SIZE),
            });

        let page = 1;
        let effectivePageSize = 0;

        return request(page).pipe(
            expand((response) => {
                const received = response.results?.length ?? 0;

                // La primera respuesta fija el tamaño real de página.
                if (page === 1) {
                    effectivePageSize = received;
                }

                const isFullPage = received > 0 && received === effectivePageSize;

                if (!isFullPage || page >= LaboratoriosService.MAX_PAGES) {
                    return EMPTY;
                }

                page += 1;
                return request(page);
            }),
            reduce((all: any[], response) => [...all, ...(response.results ?? [])], []),
        );
    }
}
